import React, { useState } from 'react';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { FormGroup, Checkbox, MenuItem, MenuList, FormControl, Divider, Typography } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { TrashIcon } from '@/components/common/Icons/TrashIcon';
import { Button } from '@/components/common/button/button';
import {
    MenuWrapper,
    MenuContent,
    MenuItemText,
    FormControlLabel,
    ButtonWrapper,
    MenuHeader,
    MenuSelectedBadge,
    MenuSelectedAllText,
} from './styles';

interface MultiSelectFilterProps {
    options: { id: string; name: string }[];
    selected: any[];
    handleRadioChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleClearSelection: () => void;
    label: string;
    prefix?: string;
    disabled?: boolean;
    menuAlignment?: 'left' | 'right';
}

export const MultiSelectFilter = (props: MultiSelectFilterProps) => {
    const {
        selected,
        options,
        handleRadioChange,
        handleClearSelection,
        label,
        prefix,
        disabled,
        menuAlignment = 'right',
    } = props;
    const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
    const [menuIsOpen, setMenuIsOpen] = useState(false);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setMenuIsOpen(true);
        setMenuAnchor(event.currentTarget);
    };

    const handleClose = () => {
        setMenuAnchor(null);
        setMenuIsOpen(false);
    };

    return (
        <React.Fragment>
            <div>
                <ButtonWrapper>
                    <Tooltip title={`${label} selection`}>
                        <Button
                            variant="tertiary"
                            size="small"
                            onClick={handleClick}
                            data-cy={`${prefix}button`}
                            endIcon={menuIsOpen ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
                            disabled={disabled}
                        >
                            <>
                                <MenuSelectedBadge color="secondary" badgeContent={selected?.length} />
                                <MenuSelectedAllText $isActive={selected?.length === 0}>All</MenuSelectedAllText>{' '}
                                {label}
                            </>
                        </Button>
                    </Tooltip>
                </ButtonWrapper>
            </div>

            <MenuWrapper
                anchorEl={menuAnchor}
                id={prefix}
                open={menuIsOpen}
                onClose={handleClose}
                elevation={0}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        mt: '20px',
                    },
                }}
                transformOrigin={{ horizontal: menuAlignment, vertical: 'top' }}
                anchorOrigin={{ horizontal: menuAlignment, vertical: 'bottom' }}
            >
                <MenuContent>
                    <>
                        <MenuHeader>
                            <Typography variant="body2">{label}</Typography>
                            <Button
                                aria-label="Reset selection"
                                onClick={handleClearSelection}
                                startIcon={<TrashIcon />}
                                size="small"
                                variant="text"
                            >
                                clear
                            </Button>
                        </MenuHeader>
                        <Divider />
                        <MenuList sx={{ maxHeight: 500, overflowY: 'scroll' }}>
                            <FormControl component="fieldset" variant="standard">
                                <FormGroup>
                                    {options.map((option) => {
                                        return (
                                            <MenuItem key={option.id}>
                                                <FormControlLabel
                                                    control={
                                                        <Checkbox
                                                            checked={!!selected?.includes(option.id)}
                                                            onChange={handleRadioChange}
                                                            data-cy={prefix ? `${prefix}checkbox-${option}` : null}
                                                            name={option.id}
                                                        />
                                                    }
                                                    label={<MenuItemText>{option.name}</MenuItemText>}
                                                />
                                            </MenuItem>
                                        );
                                    })}
                                </FormGroup>
                            </FormControl>
                        </MenuList>
                    </>
                </MenuContent>
            </MenuWrapper>
        </React.Fragment>
    );
};
